(() => {
  const VARIANT_MAP = {
    Negro: {
      '2XL': 52239715893432,
      '3XL': 52239715926200,
      '4XL': 52239715958968,
      '5XL': 52239715991736,
      '6XL': 52239716024504,
      '7XL': 52239716057272
    },
    Beige: {
      '2XL': 52239716188344,
      '3XL': 52239716221112,
      '4XL': 52239716253880,
      '5XL': 52239716286648,
      '6XL': 52239716319416,
      '7XL': 52239716352184
    }
  };

  const COLORS = Object.keys(VARIANT_MAP);
  const SIZES = ['2XL', '3XL', '4XL', '5XL', '6XL', '7XL'];
  const VALID_TIERS = new Set([1, 4, 8]);
  const DEFAULT_COLOR = 'Negro';
  const DEFAULT_SIZE = '2XL';
  const stateByBundle = new WeakMap();

  const escapeAttribute = (value) =>
    String(value).replace(/[&<>"']/g, (char) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;'
    })[char]);

  const getCartNotification = () => document.querySelector('cart-notification');
  const getCartDrawer = () => document.querySelector('cart-drawer');

  const createDefaultSelections = () =>
    Array.from({ length: 8 }, () => ({
      color: DEFAULT_COLOR,
      size: DEFAULT_SIZE
    }));

  const getState = (bundle) => {
    if (!stateByBundle.has(bundle)) {
      stateByBundle.set(bundle, {
        selections: createDefaultSelections()
      });
    }

    return stateByBundle.get(bundle);
  };

  const buildOptionMarkup = (values, selectedValue) =>
    values
      .map((value) => {
        const selected = value === selectedValue ? ' selected' : '';
        return `<option value="${escapeAttribute(value)}"${selected}>${escapeAttribute(value)}</option>`;
      })
      .join('');

  const createPairMarkup = (index, selection) => {
    const color = COLORS.includes(selection?.color) ? selection.color : DEFAULT_COLOR;
    const size = SIZES.includes(selection?.size) ? selection.size : DEFAULT_SIZE;

    return `
      <div class="thorbes-legvital-pair" data-legvital-pair data-index="${index}">
        <div class="thorbes-legvital-pair__label">Par ${index + 1}</div>
        <div class="thorbes-legvital-field">
          <label for="LegVitalColor-${index}">Color</label>
          <select id="LegVitalColor-${index}" data-legvital-color>
            ${buildOptionMarkup(COLORS, color)}
          </select>
        </div>
        <div class="thorbes-legvital-field">
          <label for="LegVitalSize-${index}">Tamaño</label>
          <select id="LegVitalSize-${index}" data-legvital-size>
            ${buildOptionMarkup(SIZES, size)}
          </select>
        </div>
      </div>
    `;
  };

  const getActiveCard = (bundle) => bundle.querySelector('[data-legvital-card].is-active');

  const getActivePanel = (bundle) => getActiveCard(bundle)?.querySelector('[data-legvital-panel]');

  const syncSelectionsFromDom = (bundle) => {
    const state = getState(bundle);
    const activePanel = getActivePanel(bundle);
    if (!activePanel) return;

    activePanel.querySelectorAll('[data-legvital-pair]').forEach((pair) => {
      const index = Number.parseInt(pair.dataset.index || '0', 10);
      if (!Number.isInteger(index) || index < 0 || index > 7) return;

      state.selections[index] = {
        color: pair.querySelector('[data-legvital-color]')?.value || DEFAULT_COLOR,
        size: pair.querySelector('[data-legvital-size]')?.value || DEFAULT_SIZE
      };
    });
  };

  const getCurrentSelections = (bundle, pairCount) => {
    syncSelectionsFromDom(bundle);
    return getState(bundle).selections.slice(0, pairCount);
  };

  const groupItems = (items) => {
    const grouped = new Map();

    for (const item of items) {
      grouped.set(item.id, (grouped.get(item.id) || 0) + item.quantity);
    }

    return Array.from(grouped.entries()).map(([id, quantity]) => ({ id: Number(id), quantity }));
  };

  const renderPairs = (bundle, pairCount) => {
    const activePanel = getActivePanel(bundle);
    const container = activePanel?.querySelector('[data-legvital-selectors]');
    if (!container) return;

    const selections = getCurrentSelections(bundle, pairCount);
    const pairs = [];

    for (let index = 0; index < pairCount; index += 1) {
      pairs.push(createPairMarkup(index, selections[index] || {
        color: DEFAULT_COLOR,
        size: DEFAULT_SIZE
      }));
    }

    container.innerHTML = pairs.join('');
  };

  const setSelectedOffer = (bundle, pairCount) => {
    const tier = Number.parseInt(pairCount, 10);
    if (!VALID_TIERS.has(tier)) return;

    syncSelectionsFromDom(bundle);
    setError(bundle);

    bundle.querySelectorAll('[data-legvital-card]').forEach((card) => {
      const button = card.querySelector('[data-legvital-offer]');
      const panel = card.querySelector('[data-legvital-panel]');
      const isSelected = Number.parseInt(card.dataset.pairs || '0', 10) === tier;

      card.classList.toggle('is-active', isSelected);
      button?.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
      button?.setAttribute('aria-expanded', isSelected ? 'true' : 'false');
      if (panel) panel.hidden = !isSelected;
    });

    renderPairs(bundle, tier);
  };

  const getSelectedPairCount = (bundle) => {
    const selected = bundle.querySelector('[data-legvital-offer][aria-pressed="true"]');
    const count = Number.parseInt(selected?.dataset.pairs || bundle.dataset.defaultOffer || '4', 10);
    return VALID_TIERS.has(count) ? count : 4;
  };

  const setError = (bundle, message = '') => {
    const activePanel = getActivePanel(bundle) || bundle;
    const error = activePanel.querySelector('[data-legvital-error]');
    if (!error) return;

    error.textContent = message;
    error.hidden = !message;
  };

  const buildItems = (bundle) => {
    const pairCount = getSelectedPairCount(bundle);
    const pairs = getCurrentSelections(bundle, pairCount);

    if (pairs.length !== pairCount) {
      throw new Error('invalid_pair_count');
    }

    const items = pairs.map(({ color, size }) => {
      if (!COLORS.includes(color) || !SIZES.includes(size)) {
        throw new Error('invalid_selection');
      }

      const variantId = VARIANT_MAP[color]?.[size];
      if (!variantId) {
        throw new Error('missing_variant');
      }

      return {
        id: variantId,
        quantity: 1
      };
    });

    const groupedItems = groupItems(items);
    const totalQuantity = groupedItems.reduce((sum, item) => sum + item.quantity, 0);

    if (totalQuantity !== pairCount) {
      throw new Error('invalid_total_quantity');
    }

    return groupedItems;
  };

  const setLoading = (bundle, isLoading) => {
    const activePanel = getActivePanel(bundle) || bundle;
    const button = activePanel.querySelector('[data-legvital-submit]');
    const text = activePanel.querySelector('[data-legvital-submit-text]');
    if (!button || !text) return;

    button.disabled = isLoading;
    button.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    text.textContent = isLoading ? bundle.dataset.loadingText : bundle.dataset.ctaText;
  };

  const parseResponseBody = (responseText) => {
    try {
      return JSON.parse(responseText);
    } catch (error) {
      return responseText;
    }
  };

  const logCartError = ({ response, endpoint, payload, responseData }) => {
    console.error('[LegVital bundle]', {
      status: response.status,
      statusText: response.statusText,
      endpoint,
      payload,
      response: responseData
    });
  };

  const getSectionInnerHTML = (html, selector = '.shopify-section') =>
    new DOMParser().parseFromString(html, 'text/html').querySelector(selector)?.innerHTML;

  const updateCartNotificationSection = (sectionId, html) => {
    if (!html) return false;

    const target = document.getElementById(sectionId);
    const nextHTML = getSectionInnerHTML(html);

    if (!target || nextHTML === undefined) return false;
    target.innerHTML = nextHTML;
    return true;
  };

  const updateCartDrawerSection = (sectionId, html) => {
    if (!html) return false;

    if (sectionId === 'cart-drawer') {
      const target = document.querySelector('#CartDrawer');
      const nextHTML = getSectionInnerHTML(html, '#CartDrawer');
      if (!target || !nextHTML) return false;
      target.innerHTML = nextHTML;
      return true;
    }

    if (sectionId === 'cart-icon-bubble') {
      return updateCartNotificationSection(sectionId, html);
    }

    return false;
  };

  const fetchCartSections = async (sectionIds) => {
    const cartUrl = window.routes?.cart_url || '/cart';
    const endpoint = `${cartUrl}?sections=${sectionIds.join(',')}`;

    const response = await fetch(endpoint, {
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      }
    });

    if (!response.ok) {
      throw new Error(`Cart sections failed: ${response.status}`);
    }

    const sections = await response.json();

    const hasAllSections = sectionIds.every((sectionId) => sections?.[sectionId]);

    if (!hasAllSections) {
      throw new Error('Cart sections response is incomplete.');
    }

    return sections;
  };

  const renderAndOpenCartNotification = async (activeElement) => {
    const cartNotification = getCartNotification();

    if (!cartNotification) {
      throw new Error('Cart notification component was not found.');
    }

    const sectionIds = ['cart-notification-product', 'cart-notification-button', 'cart-icon-bubble'];
    const sections = await fetchCartSections(sectionIds);

    const productUpdated = updateCartNotificationSection('cart-notification-product', sections['cart-notification-product']);
    const buttonUpdated = updateCartNotificationSection('cart-notification-button', sections['cart-notification-button']);
    const iconUpdated = updateCartNotificationSection('cart-icon-bubble', sections['cart-icon-bubble']);

    if (!productUpdated || !buttonUpdated || !iconUpdated || typeof cartNotification.open !== 'function') {
      throw new Error('Cart notification could not be rendered.');
    }

    cartNotification.setActiveElement?.(activeElement || document.activeElement);
    cartNotification.open();
  };

  const renderAndOpenCartDrawer = async (activeElement) => {
    const cartDrawer = getCartDrawer();

    if (!cartDrawer) {
      throw new Error('Cart drawer component was not found.');
    }

    const sectionIds = ['cart-drawer', 'cart-icon-bubble'];
    const sections = await fetchCartSections(sectionIds);

    if (typeof cartDrawer.renderContents === 'function') {
      cartDrawer.setActiveElement?.(activeElement || document.activeElement);
      cartDrawer.renderContents({
        id: 'legvital-bundle',
        sections
      });
      return;
    }

    const drawerUpdated = updateCartDrawerSection('cart-drawer', sections['cart-drawer']);
    const iconUpdated = updateCartDrawerSection('cart-icon-bubble', sections['cart-icon-bubble']);

    if (!drawerUpdated || !iconUpdated || typeof cartDrawer.open !== 'function') {
      throw new Error('Cart drawer could not be rendered.');
    }

    cartDrawer.setActiveElement?.(activeElement || document.activeElement);
    cartDrawer.open();
  };

  const renderAndOpenCart = async (activeElement) => {
    if (getCartNotification()) {
      await renderAndOpenCartNotification(activeElement);
      return;
    }

    await renderAndOpenCartDrawer(activeElement);
  };

  const addToCart = async (bundle, items) => {
    const endpoint = window.routes?.cart_add_url || '/cart/add.js';
    const payload = { items };

    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(payload)
    });

    const responseText = await response.text();
    const responseData = parseResponseBody(responseText);

    if (!response.ok || responseData.status) {
      logCartError({ response, endpoint, payload, responseData });
      throw new Error(
        responseData?.description ||
        responseData?.message ||
        `Cart add failed: ${response.status}`
      );
    }

    return responseData;
  };

  const initBundle = (bundle) => {
    if (bundle.dataset.legvitalInitialized === 'true') return;
    bundle.dataset.legvitalInitialized = 'true';

    const defaultOffer = getSelectedPairCount(bundle);
    setSelectedOffer(bundle, defaultOffer);

    bundle.querySelectorAll('[data-legvital-offer]').forEach((button) => {
      button.addEventListener('click', () => {
        setSelectedOffer(bundle, button.dataset.pairs);
      });
    });

    bundle.addEventListener('change', (event) => {
      if (!event.target.matches('[data-legvital-color], [data-legvital-size]')) return;
      syncSelectionsFromDom(bundle);
    });

    bundle.addEventListener('click', async (event) => {
      const submit = event.target.closest('[data-legvital-submit]');
      if (!submit || submit.disabled) return;
      event.preventDefault();

      let addConfirmed = false;

      try {
        setError(bundle);
        setLoading(bundle, true);
        await addToCart(bundle, buildItems(bundle));
        addConfirmed = true;
        await renderAndOpenCart(submit);
      } catch (error) {
        console.error(error);

        if (addConfirmed) {
          window.location.href = window.routes?.cart_url || '/cart';
          return;
        }

        setError(bundle, error.message || bundle.dataset.errorText || 'No fue posible agregar los productos. Intentalo nuevamente.');
      } finally {
        setLoading(bundle, false);
      }
    });
  };

  const initAll = () => {
    document.querySelectorAll('[data-legvital-bundle]').forEach(initBundle);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', initAll);
})();
